import { useEffect, useState } from 'react';

import { notifyApiError } from '@lib/bugsnag';
import { User } from '@supabase/supabase-js';

import { createClient } from '@utils/supabase/client';

interface UseSupabaseAuthReturn {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * A hook that safely gets the authenticated user from Supabase.
 * Always validates with the Supabase Auth server rather than relying on local state.
 */
export function useSupabaseAuth(): UseSupabaseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const supabase = createClient();
    // Always get fresh user data from auth server
    const fetchUser = async () => {
      try {
        setIsLoading(true);
        // This calls the Supabase Auth server to validate the token
        const { data, error: supabaseError } = await supabase.auth.getUser();

        if (supabaseError) {
          throw supabaseError;
        }

        setUser(data.user);
      } catch (err) {
        console.error('Auth error:', err);
        const apiError = notifyApiError(err, 'Client-side Supabase auth error');
        setError(apiError);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    // Initial fetch
    fetchUser();

    // Subscribe to auth changes, but still validate with getUser()
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event) => {
      // Re-fetch user to ensure we're getting validated data
      fetchUser();
    });

    // Clean up subscription
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return { user, isLoading, error };
}
